import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import './EvalMethodsArticle.css';

interface Source {
  id: string;
  name: string;
  type: string;
  url: string;
}

interface Method {
  rank: number;
  name: string;
  citations: number;
  description: string;
  aliases?: string;
  sourceIds: string[];
}

interface Tier4Method {
  name: string;
  description: string;
  sourceIds: string[];
}

const sources: Source[] = [
  { id: 'S1', name: 'BetterBench (Stanford, NeurIPS 2024)', type: 'Academic paper', url: 'https://arxiv.org/html/2411.12990v1' },
  { id: 'S2', name: 'A Survey on LLM-as-a-Judge', type: 'Academic survey', url: 'https://arxiv.org/html/2411.15594v6' },
  { id: 'S3', name: 'LLMs-as-Judges: Comprehensive Survey', type: 'Academic survey', url: 'https://arxiv.org/html/2412.05579v2' },
  { id: 'S4', name: 'Anthropic — Demystifying Evals for AI Agents', type: 'Lab guide', url: 'https://www.anthropic.com/engineering/demystifying-evals-for-ai-agents' },
  { id: 'S5', name: 'Anthropic — Statistical Approach to Model Evals', type: 'Lab research', url: 'https://www.anthropic.com/research/statistical-approach-to-model-evals' },
  { id: 'S6', name: 'OpenAI — Evaluation Best Practices', type: 'Lab guide', url: 'https://platform.openai.com/docs/guides/evaluation-best-practices' },
  { id: 'S7', name: 'Google — Stax Evaluation Best Practices', type: 'Lab guide', url: 'https://developers.google.com/stax/best-practices' },
  { id: 'S8', name: 'Google DeepMind — FACTS Benchmark', type: 'Lab research', url: 'https://deepmind.google/blog/facts-benchmark-suite-systematically-evaluating-the-factuality-of-large-language-models/' },
  { id: 'S9', name: 'Microsoft — How to Evaluate LLMs', type: 'Lab guide', url: 'https://www.microsoft.com/en-us/research/articles/how-to-evaluate-llms-a-complete-metric-framework/' },
  { id: 'S10', name: 'Hamel Husain — Your AI Product Needs Evals', type: 'Practitioner', url: 'https://hamel.dev/blog/posts/evals/' },
  { id: 'S11', name: 'Hamel Husain — Using LLM-as-a-Judge', type: 'Practitioner', url: 'https://hamel.dev/blog/posts/llm-judge/' },
  { id: 'S12', name: 'Eugene Yan — Task-Specific LLM Evals', type: 'Practitioner', url: 'https://eugeneyan.com/writing/evals/' },
  { id: 'S13', name: 'Eugene Yan — LLM Evaluators Effectiveness', type: 'Practitioner', url: 'https://eugeneyan.com/writing/llm-evaluators/' },
  { id: 'S14', name: 'Eugene Yan — Patterns for LLM Systems', type: 'Practitioner', url: 'https://eugeneyan.com/writing/llm-patterns/' },
  { id: 'S15', name: 'HuggingFace — LLM-as-a-Judge Cookbook', type: 'Framework guide', url: 'https://huggingface.co/learn/cookbook/en/llm_judge' },
  { id: 'S16', name: 'Evidently AI — LLM-as-a-Judge Guide', type: 'Framework guide', url: 'https://www.evidentlyai.com/llm-guide/llm-as-a-judge' },
  { id: 'S17', name: 'RAGAS Documentation', type: 'Framework docs', url: 'https://docs.ragas.io/en/latest/concepts/metrics/' },
  { id: 'S18', name: 'Braintrust — LLM Evaluation Metrics Guide', type: 'Framework guide', url: 'https://www.braintrust.dev/articles/llm-evaluation-metrics-guide' },
  { id: 'S19', name: 'Vals.ai — Methodology', type: 'Benchmark org', url: 'https://www.vals.ai/methodology' },
  { id: 'S20', name: 'Confident AI — LLM-as-a-Judge Guide', type: 'Framework guide', url: 'https://www.confident-ai.com/blog/why-llm-as-a-judge-is-the-best-llm-evaluation-method' },
  { id: 'S21', name: 'Inspect AI (UK AISI)', type: 'Framework docs', url: 'https://inspect.aisi.org.uk/' },
];

const tier1Methods: Method[] = [
  {
    rank: 1,
    name: 'LLM-as-a-Judge / Model-Graded Evaluation',
    citations: 18,
    description: 'Using an LLM to score or judge the output of another LLM based on criteria defined in a prompt.',
    aliases: 'Auto-rater, model-based grader, LLM evaluator, autorater',
    sourceIds: ['S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'S9', 'S10', 'S11', 'S12', 'S13', 'S14', 'S15', 'S16', 'S17', 'S18', 'S20'],
  },
  {
    rank: 2,
    name: 'Human Evaluation / Human Grading',
    citations: 15,
    description: 'Manual review and labeling of LLM outputs by human annotators, used as ground truth calibration.',
    aliases: 'Human annotation, manual labeling, human raters, expert review',
    sourceIds: ['S1', 'S2', 'S3', 'S4', 'S6', 'S7', 'S9', 'S10', 'S11', 'S12', 'S13', 'S15', 'S16', 'S18', 'S20'],
  },
  {
    rank: 3,
    name: 'Rubric-Based Scoring',
    citations: 14,
    description: 'Evaluating outputs against a structured rubric that defines what each score level means.',
    aliases: 'Criteria-based evaluation, scoring guidelines, grading scale, Likert scale',
    sourceIds: ['S1', 'S2', 'S3', 'S4', 'S6', 'S11', 'S13', 'S15', 'S16', 'S17', 'S18', 'S19', 'S20', 'S21'],
  },
  {
    rank: 4,
    name: 'Pairwise Comparison',
    citations: 13,
    description: 'Comparing two outputs side by side and selecting the better one (win/tie/loss).',
    aliases: 'A/B comparison, head-to-head evaluation, preference judgment',
    sourceIds: ['S2', 'S3', 'S4', 'S6', 'S12', 'S13', 'S14', 'S15', 'S16', 'S18', 'S19', 'S20', 'S21'],
  },
  {
    rank: 5,
    name: 'Chain-of-Thought (CoT) Prompting for Judges',
    citations: 12,
    description: 'Instructing the judge LLM to explain its reasoning step-by-step before giving a final score.',
    aliases: 'Step-by-step reasoning, evaluation rationale, explanation-based evaluation',
    sourceIds: ['S2', 'S3', 'S4', 'S6', 'S11', 'S13', 'S14', 'S15', 'S16', 'S18', 'S20', 'S21'],
  },
  {
    rank: 6,
    name: 'Few-Shot Prompting for Judges',
    citations: 11,
    description: 'Including example evaluations in the judge prompt to calibrate scoring behavior.',
    aliases: 'In-context learning for evaluation, example-guided judging',
    sourceIds: ['S2', 'S3', 'S6', 'S11', 'S13', 'S14', 'S15', 'S16', 'S18', 'S20', 'S21'],
  },
  {
    rank: 7,
    name: 'Code-Based / Rule-Based Evaluation',
    citations: 11,
    description: 'Deterministic checks using exact match, regex, string matching, or programmatic assertions.',
    aliases: 'Heuristic checks, assertion-based evaluation, unit tests for LLMs',
    sourceIds: ['S1', 'S4', 'S7', 'S9', 'S10', 'S12', 'S16', 'S17', 'S18', 'S19', 'S20'],
  },
  {
    rank: 8,
    name: 'Binary Pass/Fail Scoring',
    citations: 10,
    description: 'Simple yes/no or pass/fail judgments rather than multi-point scales.',
    aliases: 'Binary classification, yes/no evaluation, thumbs up/down',
    sourceIds: ['S2', 'S3', 'S4', 'S10', 'S11', 'S12', 'S16', 'S17', 'S18', 'S20'],
  },
  {
    rank: 9,
    name: 'Reference-Based Evaluation',
    citations: 10,
    description: 'Comparing generated output against a known-correct gold-standard reference answer.',
    aliases: 'Gold-standard comparison, ground truth matching, expected output comparison',
    sourceIds: ['S1', 'S3', 'S6', 'S12', 'S13', 'S14', 'S16', 'S17', 'S18', 'S20'],
  },
  {
    rank: 10,
    name: 'Faithfulness / Groundedness',
    citations: 10,
    description: 'Checking whether the response is grounded in provided source material without hallucination.',
    aliases: 'Hallucination detection, factual grounding, context fidelity',
    sourceIds: ['S3', 'S8', 'S9', 'S12', 'S14', 'S16', 'S17', 'S18', 'S19', 'S20'],
  },
];

const tier2Methods: Method[] = [
  {
    rank: 11,
    name: 'G-Eval',
    citations: 9,
    description: 'Framework using CoT prompting + form-filling + token probability normalization for scoring.',
    sourceIds: ['S2', 'S3', 'S13', 'S14', 'S15', 'S16', 'S18', 'S20', 'S21'],
  },
  {
    rank: 12,
    name: 'Position Bias Mitigation / Position Swapping',
    citations: 9,
    description: 'Shuffling or swapping the order of responses in pairwise comparisons to detect and reduce position bias.',
    aliases: 'Order swapping, content shuffling, debiasing',
    sourceIds: ['S2', 'S3', 'S4', 'S6', 'S11', 'S13', 'S15', 'S16', 'S20'],
  },
  {
    rank: 13,
    name: 'Human-Judge Agreement Measurement',
    citations: 9,
    description: "Measuring correlation between LLM judge scores and human annotations (Pearson, Cohen's Kappa, etc.).",
    aliases: 'Inter-rater reliability, judge validation, calibration against humans',
    sourceIds: ['S2', 'S3', 'S5', 'S10', 'S11', 'S13', 'S15', 'S16', 'S20'],
  },
  {
    rank: 14,
    name: 'Fine-Tuned Evaluator Models',
    citations: 8,
    description: 'Training specialized judge models on evaluation data (e.g., Prometheus, JudgeLM, PandaLM, Auto-J).',
    aliases: 'Custom judge models, trained evaluators, task-specific judges',
    sourceIds: ['S2', 'S3', 'S4', 'S13', 'S14', 'S16', 'S18', 'S20'],
  },
  {
    rank: 15,
    name: 'A/B Testing',
    citations: 8,
    description: 'Controlled experiments comparing two system variants with real users.',
    aliases: 'Online experimentation, controlled comparison, split testing',
    sourceIds: ['S4', 'S7', 'S9', 'S10', 'S12', 'S16', 'S18', 'S19'],
  },
  {
    rank: 16,
    name: 'Exact Match',
    citations: 7,
    description: 'Binary check of whether output exactly matches the expected answer string.',
    sourceIds: ['S1', 'S3', 'S6', 'S12', 'S17', 'S18', 'S21'],
  },
  {
    rank: 17,
    name: 'Semantic Similarity',
    citations: 7,
    description: 'Comparing outputs using embedding models (cosine similarity, BERTScore, etc.).',
    aliases: 'Embedding-based comparison, BERTScore, MoverScore',
    sourceIds: ['S3', 'S12', 'S14', 'S16', 'S17', 'S18', 'S20'],
  },
  {
    rank: 18,
    name: 'Pointwise / Direct Scoring',
    citations: 7,
    description: 'Assessing a single output independently on a numeric scale without comparison.',
    aliases: 'Single-output scoring, absolute scoring, Likert scoring',
    sourceIds: ['S2', 'S3', 'S4', 'S13', 'S15', 'S16', 'S20'],
  },
  {
    rank: 19,
    name: 'BLEU / ROUGE / Traditional NLP Metrics',
    citations: 7,
    description: 'N-gram overlap metrics comparing generated text to reference text.',
    sourceIds: ['S3', 'S12', 'S14', 'S16', 'S17', 'S18', 'S20'],
  },
  {
    rank: 20,
    name: 'Regression Testing',
    citations: 7,
    description: 'Ensuring new changes don\'t degrade performance on previously passing test cases.',
    aliases: 'Non-regression evals, golden set testing',
    sourceIds: ['S1', 'S4', 'S6', 'S7', 'S10', 'S18', 'S19'],
  },
  {
    rank: 21,
    name: 'Multi-Judge / Ensemble Evaluation',
    citations: 7,
    description: 'Using multiple LLM judges and aggregating their scores (voting, averaging, consensus).',
    aliases: 'Panel of LLM judges (PoLL), multi-LLM consensus, ensemble aggregation',
    sourceIds: ['S3', 'S4', 'S8', 'S13', 'S15', 'S16', 'S20'],
  },
  {
    rank: 22,
    name: 'Error Analysis / Failure Categorization',
    citations: 6,
    description: 'Systematically categorizing errors and failure modes to identify improvement priorities.',
    aliases: 'Root cause analysis, failure taxonomy, error classification',
    sourceIds: ['S4', 'S10', 'S11', 'S12', 'S18', 'S19'],
  },
  {
    rank: 23,
    name: 'Context Precision / Context Recall (RAG-specific)',
    citations: 6,
    description: 'Measuring whether retrieved context is relevant (precision) and complete (recall).',
    sourceIds: ['S3', 'S9', 'S12', 'S17', 'S18', 'S19'],
  },
  {
    rank: 24,
    name: 'Statistical Significance Testing',
    citations: 6,
    description: 'Computing confidence intervals, p-values, or paired-differences tests to validate score differences.',
    aliases: 'Paired-differences test, confidence intervals, SEM',
    sourceIds: ['S1', 'S2', 'S5', 'S9', 'S15', 'S19'],
  },
  {
    rank: 25,
    name: 'Listwise / Ranking Evaluation',
    citations: 5,
    description: 'Ranking multiple outputs collectively rather than comparing pairs.',
    sourceIds: ['S2', 'S3', 'S6', 'S13', 'S21'],
  },
  {
    rank: 26,
    name: 'Synthetic Data Generation for Evals',
    citations: 5,
    description: 'Using LLMs to generate test cases covering diverse scenarios and edge cases.',
    sourceIds: ['S3', 'S10', 'S11', 'S12', 'S18'],
  },
  {
    rank: 27,
    name: 'Partial Credit Scoring',
    citations: 5,
    description: 'Grading task components separately rather than all-or-nothing.',
    aliases: 'Additive scoring, component-based grading, decomposed evaluation',
    sourceIds: ['S4', 'S6', 'S15', 'S17', 'S21'],
  },
  {
    rank: 28,
    name: 'Data Contamination Detection',
    citations: 5,
    description: 'Identifying whether benchmark test data leaked into model training sets.',
    aliases: 'Canary strings, encrypted test sets, post-cutoff testing',
    sourceIds: ['S1', 'S2', 'S3', 'S8', 'S14'],
  },
];

const tier3Methods: Method[] = [
  {
    rank: 29,
    name: 'Criteria Decomposition',
    citations: 4,
    description: 'Breaking evaluation into multiple independent dimensions scored separately.',
    sourceIds: ['S2', 'S3', 'S15', 'S17'],
  },
  {
    rank: 30,
    name: 'Question-Answer Generation (QAG)',
    citations: 4,
    description: 'Breaking evaluations into atomic yes/no questions about specific claims.',
    sourceIds: ['S3', 'S12', 'S17', 'S20'],
  },
  {
    rank: 31,
    name: 'Token Probability / Logit-Based Scoring',
    citations: 4,
    description: 'Using raw output token probabilities instead of generated text for scoring.',
    sourceIds: ['S2', 'S3', 'S5', 'S20'],
  },
  {
    rank: 32,
    name: 'Self-Consistency / Self-Check',
    citations: 4,
    description: 'Generating multiple samples and measuring agreement to detect hallucinations.',
    sourceIds: ['S3', 'S13', 'S14', 'S17'],
  },
  {
    rank: 33,
    name: 'Constrained / Structured Output for Judges',
    citations: 4,
    description: 'Enforcing JSON, regex, or other structured formats on judge output for reliable parsing.',
    sourceIds: ['S2', 'S13', 'S15', 'S18'],
  },
  {
    rank: 34,
    name: 'Reward Modeling / RLHF-Based Evaluation',
    citations: 4,
    description: 'Training reward models on human preferences to score outputs.',
    sourceIds: ['S2', 'S3', 'S13', 'S14'],
  },
  {
    rank: 35,
    name: 'Eval-Driven Development',
    citations: 4,
    description: 'Writing evaluation tasks before building the system, then iterating until performance improves.',
    sourceIds: ['S4', 'S6', 'S7', 'S10'],
  },
  {
    rank: 36,
    name: 'Adversarial / Robustness Testing',
    citations: 4,
    description: 'Testing judges and models against manipulated or adversarial inputs.',
    sourceIds: ['S1', 'S2', 'S3', 'S19'],
  },
  {
    rank: 37,
    name: 'Latency / Cost Measurement',
    citations: 4,
    description: 'Tracking response time and token costs as evaluation dimensions.',
    sourceIds: ['S7', 'S9', 'S18', 'S19'],
  },
  {
    rank: 38,
    name: 'NLI-Based Factual Consistency',
    citations: 3,
    description: 'Using natural language inference models to detect contradictions between source and output.',
    sourceIds: ['S3', 'S12', 'S17'],
  },
  {
    rank: 39,
    name: 'Human-AI Collaborative Evaluation',
    citations: 3,
    description: 'Integrating human judgment with LLM evaluation in a hybrid workflow (COEVAL, EvalGen).',
    sourceIds: ['S3', 'S4', 'S10'],
  },
  {
    rank: 40,
    name: 'Domain-Specific / Specialized Judges',
    citations: 3,
    description: 'Training or prompting judges specifically for a domain (medical, legal, code, etc.).',
    sourceIds: ['S3', 'S11', 'S19'],
  },
  {
    rank: 41,
    name: 'Multi-Turn / Conversation-Level Evaluation',
    citations: 3,
    description: 'Evaluating entire multi-turn dialogues rather than single responses.',
    sourceIds: ['S4', 'S16', 'S21'],
  },
  {
    rank: 42,
    name: 'Benchmark Versioning / Dynamic Benchmarks',
    citations: 3,
    description: 'Periodically updating test sets to prevent saturation and contamination.',
    sourceIds: ['S1', 'S3', 'S14'],
  },
  {
    rank: 43,
    name: 'Coverage / Completeness Checks',
    citations: 3,
    description: 'Verifying that responses include all required key facts or components.',
    sourceIds: ['S4', 'S17', 'S18'],
  },
  {
    rank: 44,
    name: 'Pearson / Spearman / Kendall Correlation',
    citations: 3,
    description: 'Statistical correlation metrics for comparing judge rankings to ground truth.',
    sourceIds: ['S3', 'S5', 'S15'],
  },
  {
    rank: 45,
    name: 'Transcript / Trace Review',
    citations: 3,
    description: 'Manual inspection of full interaction logs to validate system behavior.',
    sourceIds: ['S4', 'S10', 'S11'],
  },
];

const tier4Methods: Tier4Method[] = [
  { name: 'Best-of-N Sampling', description: 'Generate N outputs, use judge to pick the best', sourceIds: ['S3'] },
  { name: 'Tree/Graph of Thought', description: 'Explore solution spaces with judge-evaluated branches', sourceIds: ['S3'] },
  { name: 'Meta-Rewarding', description: 'Judges themselves optimized through feedback loops', sourceIds: ['S3'] },
  { name: 'RLAIF', description: 'Reinforcement learning from AI feedback', sourceIds: ['S3'] },
  { name: 'Self-Taught Evaluator', description: 'Iterative training using synthetic contrasting outputs', sourceIds: ['S2'] },
  { name: 'Shadow Experiments', description: 'Run treatment/control simultaneously, show only control', sourceIds: ['S7', 'S9'] },
  { name: 'Edit Distance / Reformulation Tracking', description: 'Measure user prompt rewrites as dissatisfaction signal', sourceIds: ['S9'] },
  { name: 'Retention Metrics', description: 'Track user engagement over time as quality proxy', sourceIds: ['S7', 'S9'] },
  { name: 'COMETKiwi', description: 'Reference-free translation quality assessment', sourceIds: ['S12'] },
  { name: 'HyDE', description: 'Generate hypothetical docs to improve retrieval evaluation', sourceIds: ['S14'] },
  { name: 'BiasAlert', description: 'Real-time bias detection framework for judges', sourceIds: ['S3'] },
  { name: 'pass@k / pass^k', description: 'Probability of success across k attempts', sourceIds: ['S4'] },
  { name: 'Additive Scale Prompting', description: 'Award points for each atomic criterion met', sourceIds: ['S15'] },
  { name: 'Continual In-Context Learning', description: 'Dynamically select relevant examples per evaluation item', sourceIds: ['S11'] },
  { name: 'Instance-Specific Rubrics', description: 'Generate custom rubrics per input rather than using fixed ones', sourceIds: ['S17', 'S19'] },
];

const MethodCard: React.FC<{ method: Method }> = ({ method }) => (
  <div className="eval-method-card">
    <div className="eval-method-header">
      <span className="eval-method-rank">#{method.rank}</span>
      <h3 className="eval-method-name">{method.name}</h3>
      <span className="eval-citation-badge">{method.citations} sources</span>
    </div>
    <p className="eval-method-description">{method.description}</p>
    {method.aliases && (
      <p className="eval-method-aliases">
        <span className="aliases-label">Also known as:</span> {method.aliases}
      </p>
    )}
    <div className="eval-source-pills">
      {method.sourceIds.map((id) => (
        <span key={id} className="eval-source-pill">{id}</span>
      ))}
    </div>
  </div>
);

const EvalMethodsArticle: React.FC = () => {
  const [sourcesOpen, setSourcesOpen] = useState(false);

  return (
    <>
      <div className="eval-article-page">
        <div className="eval-article-container">
          <Link to="/wiki" className="back-link">← Back to Wiki</Link>

          <header className="eval-article-header">
            <h1>LLM Evaluation Methods</h1>
            <p className="eval-article-subtitle">Ranked by Citation Count</p>
            <p className="eval-article-description">
              A cross-referenced inventory of 45 evaluation methods extracted from 21 authoritative sources
              (academic papers, lab guides, practitioner blogs, and framework docs). Methods are ranked by
              number of independent sources that reference them.
            </p>
          </header>

          <section className="eval-sources-section">
            <button
              className="eval-sources-toggle"
              onClick={() => setSourcesOpen(!sourcesOpen)}
              aria-expanded={sourcesOpen}
            >
              <span className="eval-sources-toggle-icon">{sourcesOpen ? '−' : '+'}</span>
              <span>Sources Key ({sources.length} sources)</span>
            </button>
            {sourcesOpen && (
              <div className="eval-sources-table-wrapper">
                <table className="eval-sources-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Source</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((source) => (
                      <tr key={source.id}>
                        <td className="eval-source-id">{source.id}</td>
                        <td>
                          <a href={source.url} target="_blank" rel="noopener noreferrer">
                            {source.name}
                          </a>
                        </td>
                        <td className="eval-source-type">{source.type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="eval-tier-section">
            <h2 className="eval-tier-heading">
              <span className="eval-tier-label tier-1">Tier 1</span>
              Universal Methods (10+ sources)
            </h2>
            <div className="eval-methods-list">
              {tier1Methods.map((method) => (
                <MethodCard key={method.rank} method={method} />
              ))}
            </div>
          </section>

          <section className="eval-tier-section">
            <h2 className="eval-tier-heading">
              <span className="eval-tier-label tier-2">Tier 2</span>
              Widely Adopted Methods (5–9 sources)
            </h2>
            <div className="eval-methods-list">
              {tier2Methods.map((method) => (
                <MethodCard key={method.rank} method={method} />
              ))}
            </div>
          </section>

          <section className="eval-tier-section">
            <h2 className="eval-tier-heading">
              <span className="eval-tier-label tier-3">Tier 3</span>
              Specialized Methods (3–4 sources)
            </h2>
            <div className="eval-methods-list">
              {tier3Methods.map((method) => (
                <MethodCard key={method.rank} method={method} />
              ))}
            </div>
          </section>

          <section className="eval-tier-section">
            <h2 className="eval-tier-heading">
              <span className="eval-tier-label tier-4">Tier 4</span>
              Emerging / Niche Methods (1–2 sources)
            </h2>
            <div className="eval-tier4-table-wrapper">
              <table className="eval-tier4-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Description</th>
                    <th>Sources</th>
                  </tr>
                </thead>
                <tbody>
                  {tier4Methods.map((method) => (
                    <tr key={method.name}>
                      <td className="eval-tier4-name">{method.name}</td>
                      <td>{method.description}</td>
                      <td className="eval-tier4-sources">{method.sourceIds.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default EvalMethodsArticle;
